import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { conflictList, settleConflict } from "@/lib/offline/queue";
import { flushNow, refreshOfflineCounts } from "@/lib/offline/sync";

const line = (doc) =>
  [doc?.title, doc?.date, doc?.summary]
    .filter(Boolean)
    .join(" · ")
    .slice(0, 160) || "—";

const stamp = (doc) => (doc?.updated_at ? new Date(doc.updated_at).toLocaleString() : "unknown");

/** Two versions of the same entry differ — the owner picks which one stands. */
export const ConflictDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setItems(await conflictList());
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const settle = async (conflict, choice) => {
    setBusy(conflict.id);
    try {
      await settleConflict(conflict.id, choice);
      if (choice === "mine") await flushNow({ silent: true });
      await refreshOfflineCounts();
      window.dispatchEvent(new Event("diary-refresh"));
      toast.success(choice === "mine" ? "Your version was saved" : "The diary's version was kept");
      const left = await conflictList();
      setItems(left);
      if (!left.length) onOpenChange(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto" data-testid="conflict-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">These two versions differ</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          You edited this while offline and the diary changed too. Choose which one stands.
        </p>

        <div className="mt-2 space-y-4 max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="conflict-empty">
              Nothing left to review.
            </p>
          ) : null}
          {items.map((conflict) => (
            <div
              key={conflict.id}
              data-testid={`conflict-${conflict.id}`}
              className="border border-border rounded-sm p-4"
            >
              <p className="text-sm font-semibold">{conflict.label}</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="border border-border rounded-sm p-3">
                  <p className="label-caps">Your offline version</p>
                  <p className="mt-1">{line(conflict.item?.payload?.body || conflict.mine)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Edited on this device {new Date(conflict.at).toLocaleString()}
                  </p>
                </div>
                <div className="border border-border rounded-sm p-3">
                  <p className="label-caps">The diary's version</p>
                  <p className="mt-1">{line(conflict.server)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Saved {stamp(conflict.server)}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid={`conflict-keep-mine-${conflict.id}`}
                  disabled={busy === conflict.id}
                  onClick={() => settle(conflict, "mine")}
                  className="rounded-full h-11 px-5"
                >
                  Keep mine
                </Button>
                <Button
                  variant="outline"
                  data-testid={`conflict-keep-theirs-${conflict.id}`}
                  disabled={busy === conflict.id}
                  onClick={() => settle(conflict, "theirs")}
                  className="rounded-full h-11 px-5"
                >
                  Keep the diary's
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
